import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, Users, Tag, BookOpen, Upload, Database, Building2, ArrowLeft, ShieldCheck, Menu, X } from "lucide-react";
import { useAuth } from "../../lib/auth";

/**
 * Phase 7.1 — AdminShell
 * --------------------------------------------------------------
 * Premium black/gold admin layout. Sidebar (desktop) + top sheet (mobile).
 * All chrome is i18n. Designed to scale for Phase 7.2 (Content Manager),
 * 7.3 (Content Import) and 7.4 (Knowledge Base) without re-layouts.
 */
export function AdminShell({ children, active }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = [
    { key: "dashboard", to: "/admin", label: t("admin.shell.nav_dashboard", "Dashboard"), icon: LayoutDashboard, ready: true },
    { key: "users", to: "/admin/users", label: t("admin.shell.nav_users", "Admin Users"), icon: ShieldCheck, ready: true },
    { key: "businesses", to: "/admin/businesses", label: t("admin.shell.nav_businesses", "Businesses"), icon: Building2, ready: true },
    { key: "offers", to: "/admin/offers", label: t("admin.shell.nav_offers", "Offers"), icon: Tag, ready: true },
    { key: "content", to: "/admin/content", label: t("admin.shell.nav_content", "Content"), icon: BookOpen, ready: true },
    { key: "import", to: "/admin/content/import", label: t("admin.shell.nav_import", "Import"), icon: Upload, ready: true },
    { key: "kb", to: "/admin/kb", label: t("admin.shell.nav_kb", "Ask Donny KB"), icon: Database, ready: true },
  ];

  return (
    <div className="min-h-[80vh] bg-[#0B0D12] text-white" data-testid="admin-shell">
      <div className="app-container py-6 md:py-10">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="btn-ghost p-2" aria-label={t("admin.shell.back", "Back")} data-testid="admin-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="eyebrow">{t("admin.eyebrow", "\u2014 Admin Console")}</div>
              <h1 className="font-display text-2xl md:text-3xl leading-tight" data-testid="admin-shell-title">
                {t("admin.shell.title", "Admin Console")}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="btn-ghost text-xs hidden md:inline-flex items-center gap-1.5" data-testid="admin-back-to-site">
              <ArrowLeft className="w-3.5 h-3.5" /> {t("admin.shell.back_to_site", "Back to site")}
            </Link>
            <button
              type="button"
              className="lg:hidden btn-ghost p-2"
              onClick={() => setMobileOpen((s) => !s)}
              aria-label={t("admin.shell.toggle_nav", "Toggle admin navigation")}
              data-testid="admin-mobile-toggle"
            >
              {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          {/* Sidebar (desktop + collapsible mobile sheet) */}
          <aside
            className={`card-surface p-3 self-start ${mobileOpen ? "" : "hidden lg:block"}`}
            data-testid="admin-sidebar"
          >
            <nav className="flex flex-col gap-1">
              {nav.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.key;
                const className = `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-[#C8A24A]/15 text-[#E5C76B] border border-[#C8A24A]/40"
                    : "text-white/75 hover:text-white hover:bg-white/5 border border-transparent"
                } ${!item.ready ? "opacity-50 pointer-events-none" : ""}`;
                return (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    end
                    onClick={() => setMobileOpen(false)}
                    className={className}
                    data-testid={`admin-nav-${item.key}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                    {!item.ready && (
                      <span className="ms-auto text-[10px] tracking-wider uppercase gold-text">
                        {t("admin.shell.soon", "Soon")}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </nav>
            {user?.email && (
              <div className="mt-3 pt-3 border-t border-white/10 text-xs text-white/55">
                <div className="truncate">
                  {t("admin.signed_in_as", "Signed in as")}{" "}
                  <span className="text-white/85">{user.email}</span>
                </div>
              </div>
            )}
          </aside>

          <main data-testid="admin-main">{children}</main>
        </div>
      </div>
    </div>
  );
}

export default AdminShell;
